import { useState, useEffect } from 'react'
import SwaggerUI from 'swagger-ui-react'
import 'swagger-ui-react/swagger-ui.css'

import {
  getService,
  getOpenApiUrl,
  getAudience,
  getAppStage,
  getPostmanCollectionUrl,
  getStaticDocsUrl,
  setUrl,
} from './helpers'
import {
  AudienceOptionsMap,
  ServiceDisplayNamesMap,
  Services,
  Stages,
} from './constants'
import AuthForm from './AuthForm'

// This is a hack to get the auth token into the request interceptor
// as Swagger UI keeps a reference to the original request interceptor
// and doesn't update it when the state changes, so we have a reference
// to the old value in the closure. Therefore, we are using a mutable
// object to hold the auth token.
const authTokenHolder = {
  authToken: null,
}

function App() {
  const [service, setService] = useState(getService())
  const [audience, setAudience] = useState(getAudience())
  const [stage, setStage] = useState(getAppStage())
  const [authToken, setAuthToken] = useState(null)

  const [openApiUrl, setOpenApiUrl] = useState(null)
  const [postmanCollectionUrl, setPostmanCollectionUrl] = useState(null)
  const [staticDocsUrl, setStaticDocsUrl] = useState(null)

  useEffect(() => {
    setUrl({ service, audience, stage })

    setOpenApiUrl(getOpenApiUrl({ service, audience, stage }))
    setPostmanCollectionUrl(
      getPostmanCollectionUrl({ service, audience, stage }),
    )
    setStaticDocsUrl(getStaticDocsUrl({ service, audience, stage }))
  }, [service, audience, stage])

  function handleServiceChange(e) {
    setService(e.target.value)

    if (AudienceOptionsMap[e.target.value].length === 1) {
      setAudience(AudienceOptionsMap[e.target.value][0])
    }
  }

  function handleAudienceChange(e) {
    setAudience(e.target.value)
  }

  function handleStageChange(e) {
    setStage(e.target.value)
  }

  function handleAuthTokenChange(value) {
    setAuthToken(value)
    authTokenHolder.authToken = value
  }

  const serviceOptions = Object.values(Services).map((service) => (
    <option value={service} key={service}>
      {ServiceDisplayNamesMap[service]}
    </option>
  ))

  const audienceOptions = AudienceOptionsMap[service].map((audience) => (
    <option value={audience} key={audience}>
      {audience}
    </option>
  ))

  const stageOptions = Object.values(Stages).map((stage) => (
    <option value={stage} key={stage}>
      {stage}
    </option>
  ))

  const requestInterceptor = (req) => {
    req.headers.Authorization = `Bearer ${authTokenHolder.authToken}`

    return req
  }

  return (
    <div className="App">
      <section className="doc-selectors">
        <span>Select Service Docs:</span>
        <select onChange={handleServiceChange} value={service}>
          {serviceOptions}
        </select>
        {audienceOptions.length > 1 && (
          <>
            <span>Select Public or Admin:</span>
            <select onChange={handleAudienceChange} value={audience}>
              {audienceOptions}
            </select>
          </>
        )}
        <span>Select Stage:</span>
        <select onChange={handleStageChange} value={stage}>
          {stageOptions}
        </select>
      </section>
      <section className="download-links">
        <a
          href={openApiUrl}
          download={`${service}-${audience}-${stage}_openapi.json`}
          target="_blank"
          rel="noreferrer"
        >
          Download OpenAPI Spec
        </a>
        <a
          href={postmanCollectionUrl}
          download={`${service}-${audience}-${stage}_postman-collection.json`}
          target="_blank"
          rel="noreferrer"
        >
          Download Postman Collection
        </a>
        <a href={staticDocsUrl} target="_blank" rel="noreferrer">
          View Static Docs
        </a>
      </section>
      <AuthForm onAuthTokenChange={handleAuthTokenChange} stage={stage} />
      <SwaggerUI
        url={getOpenApiUrl({ service, audience, stage })}
        displayRequestDuration={!!authToken}
        requestInterceptor={requestInterceptor}
        // Disable the authorize button and operation authorize button as we are handling auth ourselves
        plugins={[
          () => ({
            components: {
              authorizeBtn: () => null,
              authorizeOperationBtn: () => null,
            },
          }),
        ]}
      />
    </div>
  )
}

export default App
